import React from "react";
import { useDispatch, useSelector } from "react-redux";
import TableHeaderRow from "../../ui/TableHeaderRow";
import TableHeader from "../../ui/TableHeader";
import Table, { Cell, Row } from "../../ui/Table";
import PropTypes from "prop-types";
import { selectAdvancedMode } from "../settings/slice";

import IDCopy from "../../ui/IDCopy";
import DropDownFilter from "../filters/DropDownFilter";
import TextFilter from "../filters/TextFilter";
import SortHeader from "../sorting/SortHeader";
import RSOFilter from "../rsos/filters/RSOFilter";
import TruncateString from "../../ui/TruncateString";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationDialog from "../../ui/DeleteConfirmationDialog";
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline";
import {
	removePriorityListsByIds,
	updatePriorityListActiveFor,
	updatePriorityListPriority,
} from "./slice";
import ErrorMsg from "../../ui/ErrorMsg";
import InputSubmit from "../../ui/InputSubmit";
import Text from "../../ui/Text";
import { selectAstrositesById } from "../astrosites/slice";
import CompactSelector from "../astrosites/CompactSelector";

/**
 * Renders lists of priorities to which filters can be applied
 */
const PriorityListTable = ({
	priorities = [],
	filter = {},
	setFilter,
	stickyHeader = true,
	selectedLists = [],
	toggleSelectedList,
	setSelectedLists,
	hideFilters = false,
	hideControls = false,
}) => {
	// User setting to show more details such as id's
	const advancedModeState = useSelector(selectAdvancedMode);
	const navigate = useNavigate();
	const navigateTo = (priority_id) => () =>
		navigate(`/priorities/${priority_id}`);

	const dispatch = useDispatch();
	const astrositesByIds = useSelector(selectAstrositesById);

	const onDelete = (id) => {
		dispatch(removePriorityListsByIds([id]));
	};

	const handleSetPriority = (priority_id, priority, _etag) => {
		priority = parseInt(priority, 10);
		dispatch(updatePriorityListPriority({ priority_id, priority, _etag }));
	};

	const allSelected =
		priorities?.length > 0 && selectedLists?.length == priorities?.length;
	const onSelectAll = () => {
		setSelectedLists(allSelected ? [] : priorities.map((p) => p.id));
	};

	const handleSetActiveFor = (priority_id, _etag) => (astrosite_ids) => {
		dispatch(
			updatePriorityListActiveFor({ priority_id, astrosite_ids, _etag }),
		);
	};

	return (
		<Table>
			<TableHeaderRow sticky={stickyHeader}>
				{toggleSelectedList && (
					<TableHeader>
						<input
							type="checkbox"
							checked={allSelected}
							onChange={onSelectAll}
							disabled={priorities?.length == 0}
						/>
					</TableHeader>
				)}
				<TableHeader
					filterControl={
						(!hideFilters && (
							<TextFilter
								filterByName="name_ctns"
								filter={filter}
								setFilter={setFilter}
								active={filter?.name_ctns ? true : false}
							/>
						)) ||
						undefined
					}
					sortControl={
						(!hideFilters && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"name"}
							/>
						)) ||
						undefined
					}
				>
					Name
				</TableHeader>
				<TableHeader>Description</TableHeader>
				<TableHeader
					sortControl={
						(!hideFilters && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"priority"}
							/>
						)) ||
						undefined
					}
				>
					Priority
				</TableHeader>
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={Object.keys(astrositesByIds).map((a) => ({
									name: a,
									value: a,
								}))}
								filterByName="active_for_astrosite"
								filter={filter}
								setFilter={setFilter}
								active={filter.active_for_astrosite ? true : false}
							/>
						)) ||
						undefined
					}
				>
					Active for
				</TableHeader>
				<TableHeader
					filterControl={
						(!hideFilters && (
							<RSOFilter
								filter={filter}
								setFilter={setFilter}
								textEntryDebounceMs={100}
								active={filter.has_norad_id ? true : false}
							/>
						)) ||
						undefined
					}
				>
					Contains RSOs
				</TableHeader>
				{/*Actions*/}
				<TableHeader />
			</TableHeaderRow>

			{priorities && (
				<tbody>
					{priorities
						?.filter((priority) => !priority.deleted)
						.map((priority) => {
							// True if we're not busy with a loading, saving, or user op
							const hideDesc =
								priority._loadStatus === "deleting" ||
								priority._saveStatus === "updating" ||
								priority._saveStatus === "rejected";

							return (
								<Row key={priority.id} zebra>
									{toggleSelectedList && (
										<Cell>
											<input
												type="checkbox"
												checked={selectedLists.includes(priority?.id)}
												onChange={() => toggleSelectedList(priority?.id)}
											/>
										</Cell>
									)}
									<Cell onClick={navigateTo(priority.id)}>
										<div>{priority.name}</div>
										{advancedModeState && (
											<IDCopy
												truncateTo={50}
												id={priority.id}
												showCopy={true}
											/>
										)}
									</Cell>
									<Cell onClick={navigateTo(priority.id)}>
										{!hideDesc && (
											<Text small secondary>
												<TruncateString
													text={priority?.description}
													at={80}
													showTitle={true}
												/>
											</Text>
										)}
										{priority._loadStatus === "deleting" && (
											<LoadingSpinnerInline />
										)}
										{priority._saveStatus === "updating" && (
											<LoadingSpinnerInline />
										)}
										{priority._saveStatus === "rejected" && (
											<ErrorMsg
												msg={priority._errorMessage}
												showMsg={false}
												showIcon={true}
											/>
										)}
									</Cell>
									<Cell>
										<InputSubmit
											handleSubmission={(priority_value) =>
												handleSetPriority(
													priority.id,
													priority_value,
													priority._etag,
												)
											}
											defaultValue={priority.priority}
											length={4}
											type="number"
											buttonName="Save"
											inline
										/>
									</Cell>
									<Cell>
										<CompactSelector
											value={priority?.active_for_astrosites}
											onChange={handleSetActiveFor(
												priority?.id,
												priority?._etag,
											)}
										/>
									</Cell>
									<Cell onClick={navigateTo(priority.id)}>
										{priority?.object_count || priority?.norad_ids?.length}
									</Cell>
									<Cell>
										{!hideControls && (
											<DeleteConfirmationDialog
												item={{ name: priority.name }}
												onDelete={() => onDelete(priority.id)}
												iconOnly
											/>
										)}
									</Cell>
								</Row>
							);
						})}
				</tbody>
			)}
		</Table>
	);
};

PriorityListTable.propTypes = {
	priorities: PropTypes.array, // the priority lists to render
	filter: PropTypes.object, // If provided will be used to power the filters if not filters will not be shown
	setFilter: PropTypes.func, // If provided will be used to power the filters if not filters will not be shown
	stickyHeader: PropTypes.bool, // If true, table header is sticky
	selectedLists: PropTypes.array, // If the list item is select it will appear in this list
	toggleSelectedList: PropTypes.func, // called when an item is selected - toggles inclusion in the list
	setSelectedLists: PropTypes.func, // sets (replaces) the selected lists
	hideFilters: PropTypes.bool, // If provided hides the filters for the table
	hideControls: PropTypes.bool, // If provided hides controls such as delete option
};

export default PriorityListTable;
